import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { AiOutlineCamera, AiOutlineUpload, AiOutlineClose, AiOutlineCheck } from 'react-icons/ai';

function getCroppedImg(imageSrc, pixelCrop) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Canvas is empty')); return; }
        const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
        resolve({ file, url: URL.createObjectURL(blob) });
      }, 'image/jpeg', 0.9);
    });
    image.src = imageSrc;
  });
}

function ImageCropper({ onDone, onCancel, currentImage }) {
  const [mode, setMode] = useState('choose'); // choose | crop
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [preview, setPreview] = useState(currentImage || null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setMode('crop');
    };
    reader.readAsDataURL(file);
  }

  async function handleCropDone() {
    try {
      const result = await getCroppedImg(imageSrc, croppedAreaPixels);
      setPreview(result.url);
      onDone(result.file, result.url);
      setMode('choose');
    } catch (e) {
      console.error('Crop error:', e);
    }
  }

  if (mode === 'crop') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-black">
        <div className="relative flex-1">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="bg-gray-900 px-4 py-3 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <span className="text-white text-sm">Zoom</span>
            <input
              type="range" min={1} max={3} step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-blue-500"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setMode('choose')}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-700 text-white"
            >
              <AiOutlineClose size={20} /> Cancel
            </button>
            <button
              onClick={handleCropDone}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 text-white font-semibold"
            >
              <AiOutlineCheck size={20} /> Use Photo
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 py-4">
      <div className="relative w-28 h-28 rounded-full overflow-hidden bg-gray-600 border-4 border-blue-500 flex items-center justify-center">
        {preview
          ? <img src={preview} alt="Profile" className="w-full h-full object-cover" />
          : <AiOutlineCamera size={40} className="text-gray-300" />
        }
      </div>
      <p className="text-gray-300 text-sm text-center">Tap to add a profile photo</p>
      <div className="flex gap-3 w-full">
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-700 text-white border border-gray-600"
        >
          <AiOutlineCamera size={20} /> Camera
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-700 text-white border border-gray-600"
        >
          <AiOutlineUpload size={20} /> Gallery
        </button>
      </div>
      {onCancel && (
        <button type="button" onClick={onCancel} className="text-gray-400 text-sm underline">
          Skip for now
        </button>
      )}
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} />
    </div>
  );
}

export default ImageCropper;
